import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

export const useDateControl = () => {
    const location = useLocation()
    const history = useHistory()
    const params = new URLSearchParams(location.search)
    const urlDate = params.get('selected_date')

    let initialDate = new Date()
    if (urlDate) {
        const parsedDate = new Date(urlDate)
        if (!isNaN(parsedDate.getTime())) {
            initialDate = parsedDate
        }
    }

    const [selectedDate, setSelectedDate] = useState<Date>(initialDate)

    // Global search deep links navigate here with a new (or, for "today", no)
    // selected_date while the page is already mounted — follow the param
    // instead of keeping the mount-time value. Equal timestamps keep the
    // current Date instance: fetch effects depend on it by identity.
    const prevUrlDateRef = useRef(urlDate)
    useEffect(() => {
        if (prevUrlDateRef.current === urlDate) {
            return
        }
        prevUrlDateRef.current = urlDate
        const parsed = urlDate ? new Date(urlDate) : new Date()
        if (!isNaN(parsed.getTime())) {
            setSelectedDate((current) => (parsed.getTime() === current.getTime() ? current : parsed))
        }
    }, [urlDate])

    const handleDateChange = useCallback(
        (date: Date) => {
            setSelectedDate(date)
            const params = new URLSearchParams(location.search)
            params.set('selected_date', date.toISOString())
            history.replace({
                pathname: location.pathname,
                search: params.toString(),
            })
        },
        [location, history],
    )

    const stepDate = useCallback(
        (step: 'increment' | 'decrement') => () => {
            const date = new Date(selectedDate)
            const newDate = new Date(date)

            if (step === 'increment') newDate.setDate(date.getDate() + 1)
            else newDate.setDate(date.getDate() - 1)

            handleDateChange(newDate)
        },
        [selectedDate, handleDateChange],
    )

    return {
        selectedDate,
        handleDateChange,
        stepDate
    }
}
